import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { gsap, ScrollTrigger } from "../lib/gsap";
import Breadcrumbs from "../components/site/Breadcrumbs";

const CAPITAL = [
  { t: "Equity Capital Raising", d: "Private placements, IPO & listing advisory, rights issues, and strategic equity partners — designed to minimise dilution and maximise strategic value." },
  { t: "Debt Capital Raising", d: "Senior secured, mezzanine, syndicated, and DFI-grade facilities across the continent — matched to your risk profile and cash-flow cycle." },
  { t: "Project & Infrastructure Finance", d: "Non-recourse and limited-recourse structures for greenfield and brownfield infrastructure in energy, mining and transport." },
  { t: "Hybrid Instruments", d: "Convertible bonds, preference shares, and royalty/streaming finance where vanilla equity or debt is suboptimal." },
  { t: "Capital Optimisation & Treasury", d: "Balance-sheet optimisation, working capital, and FX hedging strategies to protect operations against African currency volatility." },
];

const MA = [
  { t: "Mergers & Acquisitions", d: "Buy-side and sell-side advisory, plus complex cross-border and multi-jurisdictional transactions across COMESA, SADC and ECOWAS." },
  { t: "Restructuring & Turnaround", d: "Financial restructuring, operational restructuring, divestitures and spin-offs — to stabilise the balance sheet and unlock trapped value." },
  { t: "Valuation & Financial Analysis", d: "DCF, trading comparables, precedent transactions, and LBO modelling. Independent and defensible." },
  { t: "Full-Spectrum Due Diligence", d: "Integrated financial, tax, commercial and operational diligence." },
  { t: "Post-Merger Integration", d: "Synergy tracking, IT and operational harmonisation, and cultural integration to realise deal value on schedule." },
  { t: "Fairness Opinions", d: "Independent third-party validation for boards and special committees facing takeovers and related-party transactions." },
];

export default function Services() {
  const root = useRef(null);
  const stickyHeading1 = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".js-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.1, ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" } }
        );
      });
    }, root);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <div ref={root} data-testid="services-page">
      {/* HERO */}
      <section className="relative pt-0 pb-24 md:pb-32 min-h-[80vh] md:min-h-[90vh] overflow-hidden flex items-end">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/l26fol31_AdobeStock_898107757.jpeg"
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "saturate(1) brightness(0.85) contrast(1.05)" }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.55) 0%, rgba(5,9,20,0.40) 40%, rgba(5,9,20,0.80) 90%, var(--bg) 100%)" }} />
          <div className="absolute inset-x-0 top-0 h-48" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.55) 0%, transparent 100%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 25% 60%, rgba(5,9,20,0.45) 0%, rgba(5,9,20,0.10) 55%, rgba(5,9,20,0) 80%)" }} />
        </div>
        <div className="container-x relative z-10 w-full">
          <Breadcrumbs />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end mt-12 md:mt-20">
            <div className="md:col-span-7 js-reveal">
              <span className="overline" style={{ color: "#FDFBF7" }}>— Core Services</span>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] mt-6 leading-[0.92] tracking-tighter" style={{ color: "#FDFBF7" }}>
                Structuring capital<br/>
                for <em className="italic" style={{ color: "var(--gicp-gold)" }}>generational</em><br/>
                impact.
              </h1>
              <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "rgba(253,251,247,0.80)" }}>
                Two flagship practices. One execution standard. We source from commercial banks, DFIs, international
                private equity funds, family offices, and high-net-worth investors across the continent and beyond.
              </p>
            </div>
            <div className="md:col-span-5 js-reveal">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80&auto=format&fit=crop"
                  alt="Financial analysis"
                  className="w-full h-full object-cover"
                  style={{ filter: "saturate(0.85) brightness(0.9)" }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-6" style={{ background: "linear-gradient(0deg, rgba(5,9,20,0.85) 0%, transparent 100%)" }}>
                  <div className="text-xs tracking-[0.3em] uppercase" style={{ color: "var(--gicp-gold)" }}>Two Practices</div>
                  <div className="font-serif italic text-xl mt-2" style={{ color: "#FDFBF7" }}>Capital · M&amp;A</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee bg */}
        <div className="absolute inset-x-0 bottom-0 pointer-events-none opacity-[0.06] overflow-hidden select-none">
          <div className="font-serif italic whitespace-nowrap text-[20vw] leading-none tracking-tighter" style={{ color: "var(--gicp-gold)" }}>
            CAPITAL · ADVISORY · M&amp;A · DEBT · EQUITY ·
          </div>
        </div>
      </section>

      {/* CAPITAL RAISING — sticky left */}
      <section className="section" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <div ref={stickyHeading1} className="md:sticky md:top-32 js-reveal">
              <span className="overline">— Practice I</span>
              <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
                Capital<br/>
                <em className="italic" style={{ color: "var(--gicp-gold)" }}>Raising</em>.
              </h2>
              <p className="mt-8 text-base leading-relaxed max-w-md" style={{ color: "var(--fg-soft)" }}>
                Bespoke, transparent, and aligned with the specific needs of mid-to-large cap clients — from
                scale-up funding to acquisition financing and debt restructuring.
              </p>
            </div>
          </div>

          <div className="md:col-span-7 space-y-px" style={{ background: "var(--border-subtle)" }}>
            {CAPITAL.map((c, i) => (
              <div key={c.t} className="p-8 md:p-12 js-reveal" style={{ background: "var(--bg)" }} data-testid={`capital-${i}`}>
                <div className="flex items-start gap-8">
                  <div className="text-xs tracking-[0.3em] pt-2" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl md:text-4xl tracking-tight">{c.t}</h3>
                    <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>{c.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* M&A AND ADVISORY */}
      <section className="section" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 mb-16 items-center">
          <div className="md:col-span-5 js-reveal aspect-[4/3] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=80&auto=format&fit=crop"
              alt="Strategic handshake"
              className="w-full h-full object-cover"
              style={{ filter: "saturate(0.85) brightness(0.85)" }}
            />
          </div>
          <div className="md:col-span-7 js-reveal">
            <span className="overline">— Transaction Excellence</span>
            <p className="font-serif italic text-2xl md:text-4xl mt-6 leading-tight">
              &ldquo;We guide our clients through every stage of the transaction lifecycle — from strategy formulation
              to post-merger integration.&rdquo;
            </p>
          </div>
        </div>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32 js-reveal">
              <span className="overline">— Practice II</span>
              <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
                M&amp;A &<br/>
                <em className="italic" style={{ color: "var(--gicp-gold)" }}>Advisory</em>.
              </h2>
              <p className="mt-8 text-base leading-relaxed max-w-md" style={{ color: "var(--fg-soft)" }}>
                World-class transaction advisory to corporates, family-owned enterprises, and high-growth entities —
                from strategy formulation to post-merger integration.
              </p>
            </div>
          </div>

          <div className="md:col-span-7 space-y-px" style={{ background: "var(--border-subtle)" }}>
            {MA.map((c, i) => (
              <div key={c.t} className="p-8 md:p-12 js-reveal" style={{ background: "var(--bg-soft)" }} data-testid={`ma-${i}`}>
                <div className="flex items-start gap-8">
                  <div className="text-xs tracking-[0.3em] pt-2" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl md:text-4xl tracking-tight">{c.t}</h3>
                    <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--fg-soft)" }}>{c.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOURCE OF CAPITAL */}
      <section className="section">
        <div className="container-x">
          <div className="js-reveal text-center max-w-3xl mx-auto">
            <span className="overline">— Source of Capital</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-6 leading-[0.95] tracking-tight">
              A deep, institutional<br/><em className="italic" style={{ color: "var(--gicp-gold)" }}>network</em>.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-px" style={{ background: "var(--border-subtle)" }}>
            {[
              "Commercial Banks",
              "Development Finance Institutions",
              "International Private Equity",
              "Family Offices",
              "High-Net-Worth Investors",
              "Pension Funds & Insurance Companies",
            ].map((s, i) => (
              <div key={s} className="p-8 md:p-12 js-reveal" style={{ background: "var(--bg)" }}>
                <div className="text-xs tracking-[0.3em]" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</div>
                <h4 className="mt-6 font-serif text-xl md:text-2xl leading-tight">{s}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 js-reveal">
            <h2 className="font-serif text-4xl md:text-7xl leading-[0.95] tracking-tight">
              Bring us the mandate.<br/>
              <em className="italic" style={{ color: "var(--gicp-gold)" }}>We&rsquo;ll bring the execution.</em>
            </h2>
          </div>
          <div className="md:col-span-4 js-reveal">
            <Link to="/contact" data-testid="services-cta" className="btn-gold">
              Speak with our team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
